import { z } from "zod";
import type {
  CreateAssignmentPayload,
  FormType,
  VisitCategory,
} from "@/api/form-builder-assignments/formBuilderAssignments.types";
import type { CategoryDetailSearch } from "../Assignments/CategoryDetailPage/CategoryDetailPage";
import type { ScopeTarget } from "./scopeAssignments";

const WORK_ORDER_TYPES = [
  "reactive",
  "pm",
  "project",
  "survey",
  "recurring",
  "dispatch_call",
] as const satisfies readonly VisitCategory[];

const FORM_TYPES = [
  "check_in",
  "assessment",
  "further_assessment",
  "different_trade_assessment",
  "another_opinion",
  "repair",
  "different_trade_repair",
  "assumed_recall",
  "recall",
  "check_out",
  "pm_visit",
  "survey_visit",
  "walkthrough_visit",
  "recurring_visit",
  "dispatch_call",
] as const satisfies readonly FormType[];

/** The assignment slot a form is being created for; publishing it assigns the form there. */
export const assignToSchema = z.object({
  tenantId: z.string().min(1),
  clientId: z.string().min(1).optional(),
  brandId: z.string().min(1).optional(),
  category: z.enum(WORK_ORDER_TYPES),
  formType: z.enum(FORM_TYPES),
  tradeId: z.number().optional(),
  subtradeId: z.number().optional(),
  label: z.string(),
  // Carried only so the slot page can name its scope again without refetching.
  entityName: z.string().optional(),
  clientName: z.string().optional(),
  brandName: z.string().optional(),
  tradeName: z.string().optional(),
  subtradeName: z.string().optional(),
});

export type AssignTo = z.infer<typeof assignToSchema>;

export const toAssignmentPayload = (
  assignTo: AssignTo,
  templateId: string,
): CreateAssignmentPayload => ({
  tenantId: assignTo.tenantId,
  ...(assignTo.clientId ? { clientId: assignTo.clientId } : {}),
  ...(assignTo.brandId ? { brandId: assignTo.brandId } : {}),
  ...(assignTo.tradeId ? { tradeId: assignTo.tradeId } : {}),
  ...(assignTo.subtradeId ? { subtradeId: assignTo.subtradeId } : {}),
  workOrderType: assignTo.category,
  formType: assignTo.formType,
  templateId,
});

/** Search of the slot's own page, highlighting the slot. */
export const toSlotPageSearch = (assignTo: AssignTo): CategoryDetailSearch => ({
  entityId: assignTo.tenantId,
  entityName: assignTo.entityName,
  clientId: assignTo.clientId,
  clientName: assignTo.clientName,
  brandId: assignTo.brandId,
  brandName: assignTo.brandName,
  tradeId: assignTo.tradeId,
  tradeName: assignTo.tradeName,
  subtradeId: assignTo.subtradeId,
  subtradeName: assignTo.subtradeName,
  formType: assignTo.formType,
});

const sameTarget = (a: ScopeTarget, b: ScopeTarget) =>
  a.tenantId === b.tenantId && a.clientId === b.clientId && a.brandId === b.brandId;

/** The slot's own scope is assigned through `assignTo`, so it must not be created a second time. */
export const withoutSlotTarget = (targets: ScopeTarget[], slotScope: ScopeTarget) =>
  targets.filter((target) => !sameTarget(target, slotScope));
